import { useEffect, useState } from "react";

import { HeroSection } from "@/sections/HeroSection/HeroSection";
import { IdentitySection } from "@/sections/IdentitySection/IdentitySection";
import { MotionSystemSection } from "@/sections/MotionSystemSection/MotionSystemSection";
import { ServicesSection } from "@/sections/ServicesSection/ServicesSection";
import { PostHomepageSections } from "@/sections/PostHomepageSections/PostHomepageSections";
import { FooterSection } from "@/sections/FooterSection/FooterSection";
import { ContinuityRail } from "@/components/layout/ContinuityRail";
import { IdentityChrome } from "@/components/layout/IdentityChrome";
import { HorizontalTrack } from "@/components/layout/HorizontalTrack";
import { SiteMenuOverlay } from "@/components/layout/SiteMenuOverlay";
import { PRIMARY_MENU_LINKS, SITE_ROUTES } from "@/config/siteRoutes";
import { useHomepageContent } from "@/data/homepageContent";
import { useHorizontalStage } from "@/hooks/useHorizontalStage";
import { applyChromeSplitTone } from "@/utils/chromeSplitTone";
import { getSiteLocale } from "@/i18n/locale";
import "@/styles/experience.scss";

import styles from "./AppShell.module.scss";

const MOBILE_STAGE_MEDIA = "(max-width: 767px)";

const sections = [
  {
    id: "hero",
    label: "Hero",
    width: "full",
    chromeTheme: "dark",
    panelClassName: "panel panel-hero is-active",
    isAnimatedPanel: true,
  },
  {
    id: "identity",
    label: "Identity",
    width: "story",
    chromeTheme: "light",
    panelClassName: "panel panel-wide panel-story",
    isAnimatedPanel: true,
  },
  {
    id: "motion-system",
    label: "Motion",
    width: "motion",
    chromeTheme: "dark",
    panelClassName: "",
    isAnimatedPanel: false,
  },
  {
    id: "services",
    label: "Services",
    width: "services",
    chromeTheme: "light",
    panelClassName: "panel panel-services",
    isAnimatedPanel: true,
  },
] as const;

export function AppShell() {
  const homepage = useHomepageContent();
  const locale = getSiteLocale();
  const homepageSections = homepage.sections;
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window === "undefined" ? false : window.matchMedia(MOBILE_STAGE_MEDIA).matches,
  );
  const stageSections = sections;
  const { activeSectionId, scrollToSection, viewportRef } = useHorizontalStage(
    stageSections.map((section) => section.id),
  );

  useEffect(() => {
    document.title = locale === "el" && homepage.seo_title.trim().toLowerCase() === "venuex"
      ? "VenueX | Εκδηλώσεις, Branding & Ψηφιακές Εμπειρίες"
      : homepage.seo_title;

    const descriptionTag = document.querySelector('meta[name="description"]');
    descriptionTag?.setAttribute("content", homepage.search_description);
  }, [homepage.search_description, homepage.seo_title, locale]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_STAGE_MEDIA);
    const updateViewportMode = () => setIsMobileViewport(mediaQuery.matches);

    updateViewportMode();
    mediaQuery.addEventListener("change", updateViewportMode);
    return () => mediaQuery.removeEventListener("change", updateViewportMode);
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [menuOpen]);

  useEffect(() => {
    if (!isMobileViewport) {
      return;
    }

    const root = document.getElementById("homeStage");
    const chrome = root?.querySelector<HTMLElement>(".identity-chrome");
    const logo = root?.querySelector<HTMLElement>(".identity-chrome-logo") ?? null;
    const menu = root?.querySelector<HTMLElement>(".identity-chrome-menu") ?? null;
    const hero = root?.querySelector<HTMLElement>("#hero") ?? null;

    if (!root || !chrome) {
      return;
    }

    let frame = 0;

    const updateChrome = () => {
      frame = 0;

      const heroBottom = hero?.getBoundingClientRect().bottom ?? 0;
      const revealLine = Math.min(window.innerHeight * 0.18, 132);
      root.dataset.mobilePastHero = heroBottom <= revealLine ? "true" : "false";
      root.dataset.mobileChromeReady = "true";

      applyChromeSplitTone(chrome, logo);
      applyChromeSplitTone(chrome, menu);
    };

    const requestUpdate = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(updateChrome);
      }
    };

    updateChrome();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [isMobileViewport, menuOpen]);

  if (isMobileViewport) {
    return (
      <>
        <div
          id="homeStage"
          className={`${styles.appShell} ${styles.mobileSafeShell} experienceStage`}
          data-active-section="hero"
          data-chrome-visible="true"
          data-menu-open={menuOpen}
          data-mobile-past-hero="false"
          data-mobile-stage="safe"
        >
          <div className="experience-noise" />
          <IdentityChrome
            menuOpen={menuOpen}
            onToggleMenu={() => setMenuOpen((current) => !current)}
          />
          <SiteMenuOverlay
            mode="links"
            currentPath={SITE_ROUTES.home}
            items={PRIMARY_MENU_LINKS}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          />

          <div className={styles.mobileSafeFlow}>
            <section
              id="hero"
              className={`${styles.mobileSafeSection} panel panel-hero is-active`.trim()}
              data-chrome-surface="dark"
            >
              <HeroSection content={homepageSections.hero} />
            </section>

            <section
              id="identity"
              className={`${styles.mobileSafeSection} panel panel-story ${styles.mobileSafeStory}`.trim()}
              data-chrome-surface="light"
            >
              <IdentitySection content={homepageSections.identity} />
            </section>

            <section
              id="motion-system"
              className={`${styles.mobileSafeSection} ${styles.mobileSafeMotion}`.trim()}
              data-chrome-surface="dark"
            >
              <MotionSystemSection content={homepageSections.motion} />
            </section>

            <section
              id="services"
              className={`${styles.mobileSafeSection} ${styles.mobileSafeServices}`.trim()}
              data-chrome-surface="light"
            >
              <ServicesSection content={homepageSections.services} />
            </section>
          </div>
        </div>

        <PostHomepageSections content={homepageSections} />
        <FooterSection />
      </>
    );
  }

  return (
    <>
      <div
        id="homeStage"
        className={`${styles.appShell} experienceStage`}
        data-active-section={activeSectionId}
        data-chrome-visible="false"
        data-menu-open={menuOpen}
        data-mobile-stage={isMobileViewport ? "horizontal" : "default"}
      >
        <div className="experience-noise" />
        <div className="cursor-glow" />
        <IdentityChrome
          menuOpen={menuOpen}
          onToggleMenu={() => setMenuOpen((current) => !current)}
        />
        <SiteMenuOverlay
          mode="links"
          currentPath={SITE_ROUTES.home}
          items={PRIMARY_MENU_LINKS}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
        />
        <HorizontalTrack viewportRef={viewportRef}>
          <ContinuityRail content={homepageSections.continuity} />
          {stageSections.map((section) => {
            const panelClassName = section.panelClassName ?? "";
            const sectionContent = {
              hero: <HeroSection content={homepageSections.hero} />,
              identity: <IdentitySection content={homepageSections.identity} />,
              "motion-system": <MotionSystemSection content={homepageSections.motion} />,
              services: (
                <ServicesSection
                  content={homepageSections.services}
                  forceDesktopStage={isMobileViewport}
                />
              ),
            }[section.id];

            return (
              <section
                id={section.id}
                key={section.id}
                data-stage-section
                data-stage-width={section.width}
                data-stage-chrome-theme={section.chromeTheme}
                data-panel={section.isAnimatedPanel ? true : undefined}
                className={`${styles.stageSection} ${panelClassName}`.trim()}
              >
                {sectionContent}
              </section>
            );
          })}
        </HorizontalTrack>
      </div>

      {/* Outside #homeStage (which is pinned to 100svh with overflow: hidden) so
          it sits in normal document flow — once horizontal scroll bottoms out,
          useHorizontalStage's wheel handler releases the event and native
          vertical page scroll takes over to reveal this. */}
      <PostHomepageSections content={homepageSections} />
      <FooterSection />
    </>
  );
}
